import { type FC, lazy, Suspense } from "react"

import { type RichContentDocument } from "@forento/rich-content"
import type { LinkPreviewMetadata, RichContentValue } from "@forento/ui/components/RichContentEditor"

const SharedRichContentEditor = lazy(() => import("@forento/ui/components/RichContentEditor"))

type Props = {
	placeholder: string
	initialValue: RichContentDocument | null
	setValue(value: RichContentValue): void
	className?: string
}
const RichContentEditor: FC<Props> = props => (
	<Suspense
		fallback={
			<div className="border-component-border bg-component-background min-h-96 w-full animate-pulse rounded-xl border" />
		}
	>
		<SharedRichContentEditor {...props} uploadImage={uploadImage} fetchLinkPreview={fetchLinkPreview} />
	</Suspense>
)

async function uploadImage(file: File): Promise<string> {
	const formData = new FormData()
	formData.set("image", file)
	const response = await fetch("/api/rich-content/image-file", { method: "POST", body: formData })
	if (!response.ok) throw new Error("Image upload failed")
	const result: unknown = await response.json()
	if (!isRecord(result) || result.success !== 1 || !isRecord(result.file) || typeof result.file.url !== "string") {
		throw new Error("Image upload failed")
	}
	return result.file.url
}

async function fetchLinkPreview(url: string): Promise<LinkPreviewMetadata | null> {
	const response = await fetch(`/api/rich-content/link?url=${encodeURIComponent(url)}`)
	if (!response.ok) return null
	const result: unknown = await response.json()
	if (!isRecord(result) || result.success !== 1 || typeof result.link !== "string" || !isRecord(result.meta)) {
		return null
	}
	return {
		href: result.link,
		title: typeof result.meta.title === "string" ? result.meta.title : "",
		description: typeof result.meta.description === "string" ? result.meta.description : "",
		imageUrl:
			isRecord(result.meta.image) && typeof result.meta.image.url === "string" ? result.meta.image.url : null,
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}

export default RichContentEditor
